import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { ValidationSummary } from 'ui-toolkit';
import { FormFooter } from './FormFooter';
import { PartiesInformationForm } from './parties/PartiesInformationForm';
import { PropertyAddressForm } from './PropertyAddressForm';
import { TransactionInformationForm } from './TransactionInformationForm';
import { transformFromFormToService } from './utilities/transformFromFormToService';
import { createDefaultNewOrderData, generateNewOrderSchema } from './validation/NewOrderSchema';
import { LenderClientInformationContainer } from './LenderClientInformationContainer';
import { FormWrapper } from './wrappers/FormWrapper';
import { useAppDispatch } from '../../store/store';
import { Show } from '../shared/Show';
import { GroupInformationForm } from './GroupInformationForm';
import {
  useListMobilityGroupsQuery,
  useListTransactionTypesQuery,
  MobilityGroupApi,
} from '../../API/mobilityGroups/MobilityGroupApi';
import { IProgramTypeData } from '../../types/mobility/IMobilityGroup';
import { UUID } from '../../types/shared/UUID';
import { eAPITags } from '../../API/eAPITags';
import { useCreateOrderMutation } from '../../API/order/OrderApi';
import { checkForMutationSuccess } from '../../API/checkForMutationSuccess';
import { useHistory } from 'react-router-dom';
import { clientUrls } from 'ui-toolkit/rest/ClientUrlFactory';
import { eAccessControlUserType } from '../../ui-toolkit/enums/eAccessControlUserType';
import { ICreateOrderRequest } from '../../ui-toolkit/orders/interfaces/createOrder/ICreateOrderRequest';

export interface INewOrderDataEntryFormProps {
  clientId?: string;
  userType: eAccessControlUserType;
  programTypes: IProgramTypeData[];
}

export const NewOrderDataEntryForm: React.FC<INewOrderDataEntryFormProps> = ({ userType, clientId, programTypes }) => {
  const [createOrder, { error, isLoading, isSuccess }] = useCreateOrderMutation();

  const dispatch = useAppDispatch();
  const history = useHistory();

  const createOrderAction = async (createOrderData: ICreateOrderRequest) => {
    const result = await createOrder(createOrderData);
    if (checkForMutationSuccess(result, 'Order Created', 'Error Creating Order')) {
      dispatch(MobilityGroupApi.util.invalidateTags([{ type: eAPITags.MobilityGroupData, id: clientId }]));

      if (clientId) {
        history.push(clientUrls.urlFor.clientOrders());
      } else {
        history.push(clientUrls.urlFor.orderStatus(result.data.id));
      }
    }
  };

  const [isGroup, setIsGroup] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<UUID | undefined>();

  const { data: mobilityGroups = [] } = useListMobilityGroupsQuery(selectedClientId ?? '', {
    skip: !(isGroup && selectedClientId),
  });

  const { data: transactionTypes = [] } = useListTransactionTypesQuery(undefined, { skip: !!clientId });

  const newOrderValidationSchema = useMemo(() => generateNewOrderSchema(mobilityGroups), [mobilityGroups]);

  return (
    <FormWrapper userType={userType} error={error} isLoading={isLoading} isSuccess={isSuccess}>
      <Formik
        validationSchema={newOrderValidationSchema}
        initialValues={createDefaultNewOrderData(userType, clientId)}
        onSubmit={async (values) => {
          await createOrderAction(transformFromFormToService(values));
        }}
      >
        {({ handleSubmit }) => (
          <Form id="create-order-form" onSubmit={handleSubmit}>
            <ValidationSummary />
            <LenderClientInformationContainer
              canSelectClient={userType === eAccessControlUserType.Admin}
              canSelectLender={true}
              setSelectedClientId={setSelectedClientId}
              isNewOrder
            />
            <TransactionInformationForm
              autoFocus={false}
              isGroup={isGroup}
              setIsGroup={setIsGroup}
              userType={userType}
              canSelectClient={userType === eAccessControlUserType.Admin}
            />
            <Show when={isGroup && userType === eAccessControlUserType.Admin}>
              <GroupInformationForm transactionTypes={transactionTypes} programTypes={programTypes} />
            </Show>
            <PropertyAddressForm />
            <PartiesInformationForm />
            <FormFooter />
          </Form>
        )}
      </Formik>
    </FormWrapper>
  );
};
